import React from 'react';
import { Plus, Minus } from 'lucide-react';

interface RoomSelectionProps {
  value: Array<{
    type: 'single' | 'double' | 'triple' | 'suite';
    count: number;
  }>;
  onChange: (rooms: Array<{
    type: 'single' | 'double' | 'triple' | 'suite';
    count: number;
    capacity: number;
    remainingSpots: number;
  }>) => void;
  maxTravelers?: number;
}

export default function RoomSelection({ value, onChange, maxTravelers }: RoomSelectionProps) {
  const getCapacityForType = (type: 'single' | 'double' | 'triple' | 'suite') => {
    switch (type) {
      case 'single': return 1;
      case 'double': return 2;
      case 'triple': return 3;
      case 'suite': return 2;
      default: return 2;
    }
  };

  const handleRoomCountChange = (type: 'single' | 'double' | 'triple' | 'suite', count: number) => {
    const capacity = getCapacityForType(type);
    
    // Créer un nouveau tableau de chambres
    const newRooms = value.filter(room => room.type !== type);
    
    if (count > 0) {
      // Ajouter la nouvelle chambre avec la capacité
      newRooms.push({
        type,
        count,
        capacity,
        remainingSpots: capacity
      });
    }

    // Mettre à jour avec les nouvelles chambres
    onChange(newRooms);
  };

  // Calculer la capacité totale
  const totalCapacity = value.reduce((sum, room) => {
    const capacity = getCapacityForType(room.type);
    return sum + (capacity * room.count);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-blue-800 font-medium">
          Capacité totale: {totalCapacity} personne{totalCapacity > 1 ? 's' : ''}
        </p>
        {maxTravelers && totalCapacity > maxTravelers && (
          <p className="text-red-600 text-sm mt-1">
            La capacité dépasse le nombre maximum de participants ({maxTravelers})
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(['single', 'double', 'triple', 'suite'] as const).map(type => {
          const room = value.find(r => r.type === type) || { type, count: 0 };
          const capacity = getCapacityForType(type);

          return (
            <div key={type} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <h4 className="font-medium">
                    Chambre {type === 'single' ? 'Simple' :
                            type === 'double' ? 'Double' :
                            type === 'triple' ? 'Triple' : 'Suite'}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {capacity} personne{capacity > 1 ? 's' : ''} par chambre
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleRoomCountChange(type, Math.max(0, room.count - 1))}
                    className="p-1 rounded-full hover:bg-gray-100"
                    disabled={room.count === 0}
                  >
                    <Minus className="w-5 h-5 text-gray-600" />
                  </button>
                  <span className="w-8 text-center font-medium">{room.count}</span>
                  <button
                    type="button"
                    onClick={() => handleRoomCountChange(type, room.count + 1)}
                    className="p-1 rounded-full hover:bg-gray-100"
                    disabled={maxTravelers && (totalCapacity + capacity) > maxTravelers}
                  >
                    <Plus className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
              {room.count > 0 && (
                <p className="text-sm text-gray-600">
                  Capacité totale: {room.count * capacity} personne{room.count * capacity > 1 ? 's' : ''}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}